import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { useSimpleToast, getErrorMessage } from '../../components/Toast';
import { Calendar, Clock, Video, Phone, MapPin, Star, Sparkles, Send, Users, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function Interviews({ user, onLogout }) {
  const { showToast, ToastComponent } = useSimpleToast();
  const [candidates, setCandidates] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [showAIQuestions, setShowAIQuestions] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [aiQuestions, setAiQuestions] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  
  const [scheduleData, setScheduleData] = useState({
    candidate_id: '',
    interview_type: 'Technical',
    interviewer_id: '',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 60,
    mode: 'Video Call',
    meeting_link: ''
  });

  const [feedbackData, setFeedbackData] = useState({
    rating: 3,
    technical_skills: 3,
    communication: 3,
    cultural_fit: 3,
    problem_solving: 3,
    feedback: '',
    recommendation: 'Next Round'
  });

  const interviewTypes = ['Technical', 'HR', 'Managerial', 'Final'];
  const modes = ['Video Call', 'In-person', 'Phone'];
  const recommendations = ['Selected', 'Next Round', 'Rejected'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [candidatesRes, jobsRes, employeesRes] = await Promise.all([
        axios.get(`${API}/api/recruitment/candidates?stage=Screened`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/recruitment/jobs?status=Active`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/employees`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setCandidates(candidatesRes.data.candidates || []);
      setJobs(jobsRes.data.jobs || []);
      setEmployees(employeesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleGenerateQuestions = async () => {
    if (!scheduleData.candidate_id) {
      showToast('Please select a candidate first', 'warning');
      return;
    }

    const candidate = candidates.find(c => c.id === scheduleData.candidate_id);
    const job = jobs.find(j => j.id === candidate?.job_id);

    if (!job) {
      showToast('Job details not found', 'warning');
      return;
    }

    setLoadingAI(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/recruitment/generate-interview-questions`, {
        job_title: job.title,
        experience_level: job.employee_level,
        skills_required: job.skills_required || [],
        interview_type: scheduleData.interview_type,
        num_questions: 10
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAiQuestions(response.data);
      setShowAIQuestions(true);
    } catch (error) {
      console.error('AI questions error:', error);
      showToast(getErrorMessage(error), 'error');
    } finally {
      setLoadingAI(false);
    }
  };

  const handleSchedule = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/recruitment/interviews`, scheduleData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Interview scheduled successfully!', 'success');
      setShowScheduleForm(false);
      resetScheduleForm();
      fetchData();
    } catch (error) {
      console.error('Schedule interview error:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/recruitment/interviews/feedback`, {
        interview_id: selectedInterview.id,
        ...feedbackData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Feedback submitted successfully!', 'success');
      setShowFeedbackForm(false);
      setSelectedInterview(null);
      resetFeedbackForm();
    } catch (error) {
      console.error('Feedback error:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const resetScheduleForm = () => {
    setScheduleData({
      candidate_id: '',
      interview_type: 'Technical',
      interviewer_id: '',
      scheduled_date: '',
      scheduled_time: '',
      duration_minutes: 60,
      mode: 'Video Call',
      meeting_link: ''
    });
  };

  const resetFeedbackForm = () => {
    setFeedbackData({
      rating: 3,
      technical_skills: 3,
      communication: 3,
      cultural_fit: 3,
      problem_solving: 3,
      feedback: '',
      recommendation: 'Next Round'
    });
  };

  const RatingStars = ({ value, onChange, label }) => (
    <div className="mb-3">
      <label className="block text-sm font-medium mb-2">{label}</label>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="focus:outline-none"
          >
            <Star
              className={`w-6 h-6 ${star <= value ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
            />
          </button>
        ))}
        <span className="ml-2 text-sm font-medium">{value}/5</span>
      </div>
    </div>
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <ToastComponent />
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Interview Management</h1>
            <p className="text-gray-600 mt-2">Schedule and conduct interviews</p>
          </div>
          <button
            onClick={() => setShowScheduleForm(true)}
            className="px-6 py-3 text-white rounded-lg font-medium flex items-center gap-2"
            style={{backgroundColor: '#2b3380'}}
          >
            <Calendar className="w-5 h-5" />
            Schedule Interview
          </button>
        </div>

        {/* Stats Cards - Clean white design */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#e8eaf6'}}>
                <Users className="w-6 h-6" style={{color: '#2b3380'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#2b3380'}}>{candidates.length}</p>
                <p className="text-sm text-gray-500">Ready to Schedule</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#f3e5f5'}}>
                <Calendar className="w-6 h-6" style={{color: '#9c27b0'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#9c27b0'}}>0</p>
                <p className="text-sm text-gray-500">Scheduled</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#e8f5e9'}}>
                <CheckCircle className="w-6 h-6" style={{color: '#4caf50'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#4caf50'}}>0</p>
                <p className="text-sm text-gray-500">Completed</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#fff3e0'}}>
                <Star className="w-6 h-6" style={{color: '#ff9800'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#ff9800'}}>4.2</p>
                <p className="text-sm text-gray-500">Avg Rating</p>
              </div>
            </div>
          </div>
        </div>

        {/* Screened Candidates Ready for Interview */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold mb-4" style={{color: '#2b3380'}}>Candidates Ready for Interview</h2>
          {candidates.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No screened candidates available</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {candidates.map(candidate => {
                const job = jobs.find(j => j.id === candidate.job_id);
                const candidateName = candidate.name || `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() || 'Unknown';
                return (
                  <div key={candidate.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold" style={{backgroundColor: '#2b3380'}}>
                        {candidateName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-bold">{candidateName}</p>
                        <p className="text-sm text-gray-600">{job?.title || candidate.job_applied}</p>
                      </div>
                    </div>
                    <div className="text-sm space-y-1 mb-3">
                      <p><span className="text-gray-600">Exp:</span> {candidate.experience_years || candidate.experience || 0} years</p>
                      <p><span className="text-gray-600">Email:</span> {candidate.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        setScheduleData({...scheduleData, candidate_id: candidate.id});
                        setShowScheduleForm(true);
                      }}
                      className="w-full py-2 text-white rounded-lg text-sm"
                      style={{backgroundColor: '#2b3380'}}
                    >
                      Schedule Interview
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Schedule Interview Form */}
        {showScheduleForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>Schedule Interview</h2>
                <button
                  onClick={handleGenerateQuestions}
                  disabled={loadingAI || !scheduleData.candidate_id}
                  className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50"
                >
                  <Sparkles className="w-4 h-4" />
                  {loadingAI ? 'Generating...' : 'AI Questions'}
                </button>
              </div>
              
              <form onSubmit={handleSchedule}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Candidate *</label>
                    <select
                      value={scheduleData.candidate_id}
                      onChange={(e) => setScheduleData({...scheduleData, candidate_id: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    >
                      <option value="">Select Candidate</option>
                      {candidates.map(c => {
                        const cName = c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unknown';
                        return (
                          <option key={c.id} value={c.id}>{cName}</option>
                        );
                      })}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Interview Type *</label>
                    <select
                      value={scheduleData.interview_type}
                      onChange={(e) => setScheduleData({...scheduleData, interview_type: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    >
                      {interviewTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Interviewer *</label>
                    <select
                      value={scheduleData.interviewer_id}
                      onChange={(e) => setScheduleData({...scheduleData, interviewer_id: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    >
                      <option value="">Select Interviewer</option>
                      {employees.map(emp => (
                        <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Mode *</label>
                    <select
                      value={scheduleData.mode}
                      onChange={(e) => setScheduleData({...scheduleData, mode: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    >
                      {modes.map(mode => (
                        <option key={mode} value={mode}>{mode}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Date *</label>
                    <input
                      type="date"
                      value={scheduleData.scheduled_date}
                      onChange={(e) => setScheduleData({...scheduleData, scheduled_date: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Time *</label>
                    <input
                      type="time"
                      value={scheduleData.scheduled_time}
                      onChange={(e) => setScheduleData({...scheduleData, scheduled_time: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Duration (minutes)</label>
                    <input
                      type="number"
                      value={scheduleData.duration_minutes}
                      onChange={(e) => setScheduleData({...scheduleData, duration_minutes: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="15"
                      step="15"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Meeting Link</label>
                    <input
                      type="text"
                      value={scheduleData.meeting_link}
                      onChange={(e) => setScheduleData({...scheduleData, meeting_link: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="https://meet.google.com/..."
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="submit" className="px-6 py-2 text-white rounded-lg" style={{backgroundColor: '#2b3380'}}>
                    Schedule
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowScheduleForm(false); resetScheduleForm(); }}
                    className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* AI Questions Modal */}
        {showAIQuestions && aiQuestions && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-8 h-8 text-purple-500" />
                <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>
                  AI-Generated {aiQuestions.interview_type} Interview Questions
                </h2>
              </div>
              
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg mb-4">
                <p className="text-sm"><strong>Job:</strong> {aiQuestions.job_title}</p>
                <p className="text-sm"><strong>Level:</strong> {aiQuestions.experience_level}</p>
                <p className="text-sm"><strong>Model:</strong> {aiQuestions.model}</p>
              </div>

              <div className="prose max-w-none bg-white p-6 rounded-lg border">
                <pre className="whitespace-pre-wrap text-sm">{aiQuestions.questions}</pre>
              </div>

              <button
                onClick={() => setShowAIQuestions(false)}
                className="mt-4 px-6 py-2 bg-gray-300 text-gray-700 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Interviews;